// Kleine Darstellungsbausteine der Ticket-Liste (design_handoff_tickets §1).
import type { CSSProperties, ReactNode } from 'react';
import {
  ArrowCounterClockwise, CheckCircle, CircleDashed, GearSix, HourglassMedium, LockSimple, Sparkle, Truck, WarningCircle, type Icon,
} from '@phosphor-icons/react';
import { STATUS_PILL, PRIO_PILL } from '../../../../components/ui/statusStyles';
import { PRIO_FARBE, STATUS_FARBE } from '../../../../lib/tickets/darstellung';

/** Icon je Status (Prototyp). */
/**
 * Native Auswahl-Popups (v. a. Chrome unter Windows) übernehmen die helle Schriftfarbe des Feldes,
 * zeichnen den Hintergrund aber systemweiß → leere weiße Liste im Dunkelmodus (#513 Bild 2).
 * Deshalb Hintergrund und Schrift der Einträge ausdrücklich setzen.
 */
export const OPTION_STIL: CSSProperties = { background: 'var(--bg)', color: 'var(--ink)' };

export const STATUS_ICON: Record<string, Icon> = {
  neu: Sparkle,
  offen: CircleDashed,
  in_bearbeitung: GearSix,
  warten_auf_rueckmeldung: HourglassMedium,
  warten_auf_dienstleister: Truck,
  eskaliert: WarningCircle,
  erledigt: CheckCircle,
  geschlossen: LockSimple,
};
export const STATUS_ICON_STANDARD: Icon = ArrowCounterClockwise;

export const statusLabel = (s: string) => STATUS_PILL[s]?.label ?? s;
export const prioLabel = (p: string) => PRIO_PILL[p]?.label ?? p;
export const statusFarbe = (s: string) => STATUS_FARBE[s] ?? 'var(--ink3)';
export const prioFarbe = (p: string) => PRIO_FARBE[p] ?? 'var(--ink3)';

/** Pill mit Punkt: h24, 11/800, Fläche = Farbe 14 %. */
export function FarbPill({ farbe, children, punkt = true, style }: { farbe: string; children: ReactNode; punkt?: boolean; style?: CSSProperties }) {
  return (
    <span className="inline-flex shrink-0 items-center whitespace-nowrap" style={{
      height: 24, padding: '0 9px', gap: 6, borderRadius: 12, fontSize: 11, fontWeight: 800,
      background: `color-mix(in srgb, ${farbe} 14%, transparent)`, color: farbe, ...style,
    }}>
      {punkt && <span aria-hidden style={{ width: 6, height: 6, borderRadius: '50%', background: farbe }} />}
      {children}
    </span>
  );
}

export const TicketStatusPill = ({ status }: { status: string }) => <FarbPill farbe={statusFarbe(status)}>{statusLabel(status)}</FarbPill>;
export const TicketPrioPill = ({ prio }: { prio: string }) => <FarbPill farbe={prioFarbe(prio)}>{prioLabel(prio)}</FarbPill>;

/** Initialen-Kreis (Zuständig). */
export function KleinAvatar({ name, team = false, groesse = 26 }: { name: string; team?: boolean; groesse?: number }) {
  const teile = name.trim().split(/\s+/).filter(Boolean);
  const ini = teile.length > 1 ? `${teile[0]![0]}${teile[teile.length - 1]![0]}` : (teile[0] ?? '?').slice(0, 2);
  return (
    <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{
      width: groesse, height: groesse, borderRadius: team ? 8 : '50%', fontSize: groesse < 30 ? 10 : 11, fontWeight: 800,
      background: 'var(--acc-soft)', color: 'var(--acc)', textTransform: 'uppercase',
    }}>{ini}</span>
  );
}

/** Kennzahl-Widget oben (Klick = Korb). */
export function KennzahlKachel({ label, zahl, farbe, icon: I, aktiv, onClick, tip }: {
  label: string; zahl: number; farbe: string; icon: Icon; aktiv: boolean; onClick: () => void; tip: string;
}) {
  return (
    <button type="button" onClick={onClick} aria-pressed={aktiv} data-tip={tip}
      className="immo-glas immo-kennzahl relative flex min-w-0 flex-col items-start overflow-hidden text-left"
      style={{ borderRadius: 24, padding: 16, minHeight: 118, gap: 10, border: 'none', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--ink)',
        boxShadow: aktiv ? `var(--glass-sh), inset 0 0 0 2px ${farbe}` : undefined }}>
      <I aria-hidden size={84} weight="fill" className="pointer-events-none absolute" style={{ right: -10, bottom: -16, opacity: 0.1, color: farbe }} />
      <span className="flex w-full items-center justify-between">
        <span aria-hidden className="inline-flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 11, background: `color-mix(in srgb, ${farbe} 15%, transparent)`, color: farbe }}>
          <I size={18} weight="fill" />
        </span>
        {aktiv && <CheckCircle aria-hidden size={18} weight="fill" color={farbe} />}
      </span>
      <span className="relative tabular-nums" style={{ fontSize: 30, fontWeight: 800, lineHeight: 1, letterSpacing: '-0.03em', color: zahl === 0 ? 'var(--ink3)' : farbe }}>{zahl}</span>
      <span className="relative" style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink2)', lineHeight: 1.2 }}>{label}</span>
    </button>
  );
}

/** Arbeitskorb-Chip (h34, aktiv = Akzentfläche). */
export function KorbChip({ label, anzahl, icon: I, aktiv, onClick }: { label: string; anzahl: number; icon: Icon; aktiv: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={aktiv} data-tip={`${label} – Arbeitskorb: zeigt nur die passenden Tickets`}
      className={`${aktiv ? '' : 'immo-glas'} inline-flex shrink-0 items-center whitespace-nowrap`}
      style={{ height: 34, padding: '0 8px 0 12px', gap: 7, borderRadius: 17, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
        ...(aktiv ? { background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' } : { color: 'var(--ink)' }) }}>
      <I aria-hidden size={15} weight={aktiv ? 'fill' : 'bold'} />
      {label}
      <span className="inline-flex items-center justify-center tabular-nums" style={{ minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, fontSize: 10.5, fontWeight: 800,
        background: aktiv ? 'var(--acc-ink-fill)' : 'var(--fill2)', color: aktiv ? 'var(--acc-ink)' : 'var(--ink2)' }}>{anzahl}</span>
    </button>
  );
}

/** Filter-Chip mit Farbpunkt und Zähler (h32). */
export function FilterChip({ label, anzahl, farbe, aktiv, onClick }: { label: string; anzahl: number; farbe: string; aktiv: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={aktiv} data-tip={`${label} – Filter ein- oder ausschalten (${anzahl} Tickets)`}
      className="inline-flex shrink-0 items-center whitespace-nowrap"
      style={{ height: 32, padding: '0 11px', gap: 7, borderRadius: 16, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700,
        ...(aktiv
          ? { background: `color-mix(in srgb, ${farbe} 16%, transparent)`, boxShadow: `inset 0 0 0 1.5px ${farbe}`, color: 'var(--ink)' }
          : { background: 'var(--fill2)', color: 'var(--ink2)' }) }}>
      <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: farbe }} />
      {label}
      <span className="tabular-nums" style={{ opacity: 0.6 }}>{anzahl}</span>
    </button>
  );
}

/** Runder Glas-Icon-Knopf (Aktualisieren u. ä.). */
export function RundKnopf({ icon: I, label, tip, onClick, groesse = 44, dreht = false }: { icon: Icon; label: string; tip?: string; onClick: () => void; groesse?: number; dreht?: boolean }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} data-tip={tip ?? label}
      className="immo-glas inline-flex shrink-0 items-center justify-center"
      style={{ width: groesse, height: groesse, borderRadius: '50%', border: 'none', cursor: 'pointer', color: 'var(--ink)' }}>
      <I size={17} weight="bold" className={dreht ? 'animate-spin' : undefined} />
    </button>
  );
}
